import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Icons } from '../lib/Icons';

const HelpProgressTrackingPage = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', name: 'Overview', IconComponent: Icons.ChartBar },
    { id: 'dashboard', name: 'Progress Dashboard', IconComponent: Icons.ArrowTrendingUp },
    { id: 'tracking-methods', name: 'Tracking Methods', IconComponent: Icons.Target },
    { id: 'reports', name: 'Reports & Analytics', IconComponent: Icons.ClipboardDocumentList },
    { id: 'best-practices', name: 'Best Practices', IconComponent: Icons.LightBulb }
  ];

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center"><Icons.ChartBar className="w-8 h-8 mr-3" /> Progress Tracking Help</h1>
              <p className="text-gray-600 mt-2">
                Complete guide to monitoring student development and achievements
              </p>
            </div>
            <Link
              to="/progress-dashboard"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Progress Dashboard
            </Link>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white border-b border-gray-200 rounded-lg shadow-sm mb-8">
          <div className="px-6">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.IconComponent className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'overview' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Progress Tracking Overview</h2>
                <p className="text-gray-600 mb-4">
                  Progress tracking is essential for monitoring student development, identifying areas of 
                  strength and need, and ensuring that interventions and curricula are effective. The system 
                  provides comprehensive tools for tracking progress across multiple areas and generating 
                  meaningful insights.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.ArrowTrendingUp className="w-8 h-8 text-blue-600" /></div>
                    <h3 className="font-semibold text-gray-900">Real-time Monitoring</h3>
                    <p className="text-sm text-gray-600 mt-2">Track progress as it happens</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.Target className="w-8 h-8 text-green-600" /></div>
                    <h3 className="font-semibold text-gray-900">Goal Tracking</h3>
                    <p className="text-sm text-gray-600 mt-2">Monitor achievement of objectives</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.ChartBar className="w-8 h-8 text-purple-600" /></div>
                    <h3 className="font-semibold text-gray-900">Data Insights</h3>
                    <p className="text-sm text-gray-600 mt-2">Generate meaningful reports</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'dashboard' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Progress Dashboard</h2>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Dashboard Overview</h3>
                    <p className="text-blue-800 mb-3">
                      The Progress Dashboard provides a comprehensive view of student progress across 
                      all areas, with filtering options and detailed insights.
                    </p>
                    <ul className="text-blue-800 space-y-1">
                      <li>• Overall progress statistics</li>
                      <li>• Student performance summaries</li>
                      <li>• Progress trends over time</li>
                      <li>• Areas requiring attention</li>
                    </ul>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-900 mb-2">Key Metrics</h3>
                      <ul className="text-green-800 space-y-1">
                        <li>• Total students</li>
                        <li>• Average progress percentage</li>
                        <li>• Students on track</li>
                        <li>• Students needing attention</li>
                      </ul>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-purple-900 mb-2">Filtering Options</h3>
                      <ul className="text-purple-800 space-y-1">
                        <li>• Filter by student</li>
                        <li>• Filter by subject area</li>
                        <li>• Filter by progress level</li>
                        <li>• Date range filtering</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-yellow-900 mb-2">Navigation Tips</h3>
                    <ul className="text-yellow-800 space-y-1">
                      <li>• Use the search bar to find specific students</li>
                      <li>• Click on student cards for detailed views</li>
                      <li>• Apply filters to focus on specific areas</li>
                      <li>• Export data for external analysis</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tracking-methods' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Tracking Methods</h2>
                
                <div className="space-y-6">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.PencilSquare className="w-5 h-5 mr-2" /> Regular Assessments</h3>
                    <p className="text-gray-600 mb-2">
                      Conduct regular assessments to measure student progress against learning objectives.
                    </p>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Weekly or monthly progress checks</li>
                      <li>• Formal and informal assessments</li>
                      <li>• Observation-based evaluations</li>
                      <li>• Student self-assessment</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.Target className="w-5 h-5 mr-2" /> Goal Monitoring</h3>
                    <p className="text-gray-600 mb-2">
                      Track progress towards specific learning goals and objectives.
                    </p>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Set SMART (Specific, Measurable, Achievable, Relevant, Time-bound) goals</li>
                      <li>• Break down goals into smaller milestones</li>
                      <li>• Regular review of goal progress</li>
                      <li>• Adjust goals based on progress</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.ChartBar className="w-5 h-5 mr-2" /> Data Collection</h3>
                    <p className="text-gray-600 mb-2">
                      Collect and record various types of progress data.
                    </p>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Academic achievement data</li>
                      <li>• Behavioral observations</li>
                      <li>• Social interaction records</li>
                      <li>• Communication development</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-orange-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.ArrowPath className="w-5 h-5 mr-2" /> Continuous Monitoring</h3>
                    <p className="text-gray-600 mb-2">
                      Implement continuous monitoring systems for ongoing progress tracking.
                    </p>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Daily observation notes</li>
                      <li>• Weekly progress summaries</li>
                      <li>• Monthly comprehensive reviews</li>
                      <li>• Term-based assessments</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Reports & Analytics</h2>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center"><Icons.ClipboardDocumentList className="w-5 h-5 mr-2" /> Progress Reports</h3>
                      <ul className="text-blue-800 space-y-1">
                        <li>• Individual student reports</li>
                        <li>• Class or group summaries</li>
                        <li>• Subject-specific progress</li>
                        <li>• Intervention effectiveness</li>
                      </ul>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-900 mb-2 flex items-center"><Icons.ChartBar className="w-5 h-5 mr-2" /> Analytics</h3>
                      <ul className="text-green-800 space-y-1">
                        <li>• Progress trends over time</li>
                        <li>• Comparative analysis</li>
                        <li>• Achievement patterns</li>
                        <li>• Performance insights</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-purple-900 mb-2 flex items-center"><Icons.ArrowTrendingUp className="w-5 h-5 mr-2" /> Report Types</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-purple-900 mb-2">Regular Reports</h4>
                        <ul className="text-purple-800 text-sm space-y-1">
                          <li>• Weekly progress updates</li>
                          <li>• Monthly summaries</li>
                          <li>• Term reports</li>
                          <li>• Annual reviews</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-purple-900 mb-2">Special Reports</h4>
                        <ul className="text-purple-800 text-sm space-y-1">
                          <li>• Intervention evaluations</li>
                          <li>• Achievement celebrations</li>
                          <li>• Areas of concern</li>
                          <li>• Recommendations</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-orange-900 mb-2 flex items-center"><Icons.ArrowUp className="w-5 h-5 mr-2" /> Sharing Reports</h3>
                    <ul className="text-orange-800 space-y-1">
                      <li>• Share with parents/guardians</li>
                      <li>• Provide to other staff members</li>
                      <li>• Include in student records</li>
                      <li>• Use for planning meetings</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'best-practices' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Best Practices</h2>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center"><Icons.Calendar className="w-5 h-5 mr-2" /> Regular Monitoring</h3>
                    <ul className="text-blue-800 space-y-1">
                      <li>• Establish consistent monitoring schedules</li>
                      <li>• Use standardized assessment tools</li>
                      <li>• Record observations promptly</li>
                      <li>• Review progress regularly</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-green-900 mb-2 flex items-center"><Icons.Target className="w-5 h-5 mr-2" /> Goal Setting</h3>
                    <ul className="text-green-800 space-y-1">
                      <li>• Set realistic and achievable goals</li>
                      <li>• Involve students in goal setting</li>
                      <li>• Break down complex objectives</li>
                      <li>• Celebrate small achievements</li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-purple-900 mb-2 flex items-center"><Icons.ChartBar className="w-5 h-5 mr-2" /> Data Quality</h3>
                    <ul className="text-purple-800 space-y-1">
                      <li>• Ensure data accuracy and consistency</li>
                      <li>• Use multiple sources of evidence</li>
                      <li>• Document assessment methods</li>
                      <li>• Maintain data security</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-yellow-900 mb-2 flex items-center"><Icons.Handshake className="w-5 h-5 mr-2" /> Collaboration</h3>
                    <ul className="text-yellow-800 space-y-1">
                      <li>• Work with other staff members</li>
                      <li>• Involve parents/guardians</li>
                      <li>• Consult with specialists</li>
                      <li>• Share best practices</li>
                    </ul>
                  </div>

                  <div className="mt-8 bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center"><Icons.LightBulb className="w-5 h-5 mr-2" /> Pro Tips:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Use visual progress charts for easy understanding</li>
                      <li>• Set up automated progress reminders</li>
                      <li>• Create progress celebration rituals</li>
                      <li>• Regular training on assessment methods</li>
                      <li>• Keep detailed records for future reference</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default HelpProgressTrackingPage; 